import React, { FC, useState, useEffect } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { useChatGraphQL } from './hooks/useChatGraphQL'
import { ConversationList } from './components/ConversationList'
import { ChatWindow } from './components/ChatWindow'
import { ContactCrmPanel } from './components/ContactCrmPanel'
import { resolveConversationChannel } from './helpers/channelBadgeHelper'
import { NewChatModal, ContactSearchResult as GraphQLContactResult } from './components/NewChatModal'
import { ContactSearchResult } from './hooks/useChatGraphQL'
import { parseId } from '../../helpers'
import {useIntl} from 'react-intl'
import {CHAT_MESSAGES as C} from './chatMessages'

const InboxPage: FC = () => {
  const intl = useIntl()
    const {
        conversations,
        currentConversation,
        setCurrentConversation,
        messages,
        loadingInbox,
        loadingMessages,
        sendMessage,
        sendMessageWithMedia,
        startConversation,
        inboxMode,
        setInboxMode,
        isAdmin,
        isSupervisor,
        isSuperAdmin,
        // Filters
        filterUnread, setFilterUnread,
        filterTags, setFilterTags,
        filterDateFrom, setFilterDateFrom,
        filterDateTo, setFilterDateTo,
        setFilterSearch,
        filterUserId, setFilterUserId,
        filterOrgId, setFilterOrgId,
        orgOptions,
        fetchOrgOptions,
        availableTags,
        createTag,
        manageTag,
        configSource,
        availableConfigs,
        inboxUsers,
        fetchInboxUsers,
        reloadInbox,
        // Pagination
        currentPage,
        setCurrentPage,
        pageSize,
        totalConversations,
        channelUnread
    } = useChatGraphQL()

    const [showNewChat, setShowNewChat] = useState(false)

    // Client-side channel filters (fast, no backend trip needed).
    // No "All" — the list is always scoped to one channel; default WhatsApp.
    const [filterWhatsapp, setFilterWhatsapp] = useState(true)
    const [filterSms, setFilterSms] = useState(false)

    // Raw input — debounced 500 ms into the backend search param
    const [rawSearch, setRawSearch] = useState('')

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilterSearch(rawSearch)
            setCurrentPage(0)
        }, 500)
        return () => clearTimeout(timer)
    }, [rawSearch, setFilterSearch, setCurrentPage])

    // The active channel is driven by the WhatsApp/SMS tab; the list is always scoped
    // to exactly one channel by the conversation's ACTUAL channel (name/phone search is
    // server-side), and a new chat opens on this same channel.
    const activeChannel: 'WHATSAPP' | 'SMS' = filterSms && !filterWhatsapp ? 'SMS' : 'WHATSAPP'
    const filteredConversations = conversations.filter(c => resolveConversationChannel(c) === activeChannel)

    // Express new chat: pick a contact and jump straight into the thread on the current
    // channel — no channel/template modal in between. WhatsApp cold-start still offers
    // the "Send template" button inside the thread when the 24h window is closed.
    const handleContactSelected = (contact: GraphQLContactResult) => {
        const mappedContact: ContactSearchResult = {
            id: Number(parseId(contact.id)), // Decode Relay Global ID (base64) to numeric PK
            full_name: contact.fullName,
            phone: contact.phone,
            email: contact.email
        }
        setShowNewChat(false)
        startConversation(mappedContact)
    }


    return (
        <>
            <PageTitle breadcrumbs={[]}>{intl.formatMessage(C.inbox)}</PageTitle>

            <div className='d-flex flex-column flex-lg-row inbox-page'>
                {/* Sidebar */}
                <div className='flex-column flex-lg-row-auto w-100 w-lg-300px w-xl-400px mb-10 mb-lg-0'>
                    <ConversationList
                        conversations={filteredConversations}
                        selectedId={currentConversation?.id}
                        onSelect={setCurrentConversation}
                        onNewChat={() => setShowNewChat(true)}
                        onRefresh={reloadInbox}
                        loading={loadingInbox}
                        searchQuery={rawSearch}
                        onSetSearchQuery={setRawSearch}
                        inboxMode={inboxMode}
                        onSetMode={setInboxMode}
                        isAdmin={isAdmin}
                        isSupervisor={isSupervisor}
                        filterWhatsapp={filterWhatsapp}
                        onToggleWhatsapp={setFilterWhatsapp}
                        filterSms={filterSms}
                        onToggleSms={setFilterSms}
                        channelUnread={channelUnread}

                        filterUnread={filterUnread}
                        onToggleUnread={setFilterUnread}
                        filterDateFrom={filterDateFrom}
                        onSetDateFrom={setFilterDateFrom}
                        filterDateTo={filterDateTo}
                        onSetDateTo={setFilterDateTo}
                        filterTags={filterTags}
                        onSetTags={setFilterTags}
                        availableTags={availableTags}
                        filterUserId={filterUserId}
                        onSetFilterUserId={(id) => { setFilterUserId(id); setCurrentPage(0) }}
                        inboxUsers={inboxUsers}
                        onFetchInboxUsers={fetchInboxUsers}
                        isSuperAdmin={isSuperAdmin}
                        filterOrgId={filterOrgId}
                        onSetFilterOrgId={(id) => { setFilterOrgId(id); setCurrentPage(0) }}
                        orgOptions={orgOptions}
                        onFetchOrgOptions={fetchOrgOptions}
                        currentPage={currentPage}
                        onSetPage={setCurrentPage}
                        pageSize={pageSize}
                        totalItems={totalConversations}
                    />
                </div>

                {/* Content */}
                <div className='flex-lg-row-fluid ms-lg-7 ms-xl-10'>
                    <ChatWindow
                        activeConversation={currentConversation}
                        messages={messages}
                        loading={loadingMessages}
                        onSendMessage={sendMessage}
                        onSendMessageWithMedia={sendMessageWithMedia}
                        availableTags={availableTags}
                        onCreateTag={createTag}
                        onManageTag={manageTag}
                        configSource={configSource}
                        availableConfigs={availableConfigs}
                        preferredChannel={activeChannel}
                    />
                </div>

                {/* CRM context panel (right) */}
                {currentConversation && (
                    <div className='flex-column flex-lg-row-auto w-100 w-lg-300px w-xl-340px ms-lg-7 mt-10 mt-lg-0'>
                        <ContactCrmPanel conversation={currentConversation} />
                    </div>
                )}
            </div>

            <NewChatModal
                show={showNewChat}
                onHide={() => setShowNewChat(false)}
                onSelect={handleContactSelected}
            />
        </>
    )
}

export { InboxPage }
